import { getWalletProviderId } from '@/utils/auth-token';
import { ensureCustodialGas } from '@/api/custodial-gas';
import { getWalletSigningProvider } from '@/utils/wallet-provider';
import { getExpectedChainIdHex } from '@/utils/wallet-chain';

function toHexQuantity(value) {
  if (value == null) return undefined;
  if (typeof value === 'string' && value.startsWith('0x')) return value;
  const n = typeof value === 'bigint' ? value : BigInt(value);
  if (n <= 0n) return '0x0';
  return `0x${n.toString(16)}`;
}

function resolveGasLimitHex(populated, estimatedHex) {
  const raw = populated?.gasLimit ?? populated?.gas;
  if (raw != null) {
    const n = typeof raw === 'bigint' ? raw : BigInt(raw);
    if (n > 0n) return toHexQuantity(n);
  }
  return estimatedHex;
}

/**
 * Privy：populate → gas 映射为 gasLimit → sign → sendRaw
 * （勿用 eth_sendTransaction，iframe 签名只认 gasLimit）
 */
export async function sendPrivyContractTransaction(browserProvider, { from, to, data, value }) {
  const provider = await getWalletSigningProvider();
  if (!provider?.request) {
    throw new Error('wallet provider not found');
  }

  const chainId = getExpectedChainIdHex();
  const baseTx = { from, to, data, chainId };
  const valueHex = toHexQuantity(value);
  if (valueHex && valueHex !== '0x0') {
    baseTx.value = valueHex;
  }

  const populated = await provider.request({
    method: 'eth_populateTransactionRequest',
    params: [baseTx],
  });

  const estimated = await provider.request({
    method: 'eth_estimateGas',
    params: [baseTx],
  });
  const withBuffer = (BigInt(estimated) * 120n) / 100n;
  const minGas = 60000n;
  const gasLimitHex = resolveGasLimitHex(
    populated,
    toHexQuantity(withBuffer < minGas ? minGas : withBuffer)
  );

  const signPayload = {
    ...populated,
    from,
    to,
    data,
    chainId: populated.chainId ?? chainId,
    gasLimit: gasLimitHex,
  };
  delete signPayload.gas;

  const signed = await provider.request({
    method: 'eth_signTransaction',
    params: [signPayload],
  });

  const txHash = await provider.request({
    method: 'eth_sendRawTransaction',
    params: [signed],
  });

  return {
    hash: txHash,
    wait: (confirms) => browserProvider.waitForTransaction(txHash, confirms),
  };
}

/** Privy 走三步 RPC；插件钱包走 ethers sendTransaction */
export async function sendContractTransaction(signer, browserProvider, populatedTx) {
  const from = await signer.getAddress();

  if (getWalletProviderId() === 'privy') {
    await ensureCustodialGas(from);
    return sendPrivyContractTransaction(browserProvider, {
      from,
      to: populatedTx.to,
      data: populatedTx.data,
      value: populatedTx.value,
    });
  }

  const request = { ...populatedTx, from };
  const gasEstimate = await signer.estimateGas(request);
  let gasLimit = (gasEstimate * 120n) / 100n;
  const minGas = 60000n;
  if (gasLimit < minGas) gasLimit = minGas;
  return signer.sendTransaction({ ...request, gasLimit });
}

export async function waitForContractTransaction(tx) {
  const receipt = await tx.wait();
  if (receipt?.status === 0) {
    throw new Error('transaction failed');
  }
  return receipt;
}
